import { z } from 'zod';
import { UuidSchema } from './ids';
import { TextContentSchema } from './richText';

export const OptionSchema = z
  .object({
    option_id: UuidSchema,
    label: TextContentSchema,
  })
  .strict();
export type Option = z.infer<typeof OptionSchema>;

const HeadingBlock = z
  .object({
    type: z.literal('heading'),
    block_id: UuidSchema,
    level: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal(6)]).default(1),
    text: TextContentSchema,
  })
  .strict();

const TextBlock = z
  .object({
    type: z.literal('text'),
    block_id: UuidSchema,
    text: TextContentSchema,
  })
  .strict();

const ShortTextBlock = z
  .object({
    type: z.literal('short_text'),
    block_id: UuidSchema,
    label: TextContentSchema,
    required: z.boolean().default(false),
    input_type: z.enum(['text', 'email', 'tel', 'url']).default('text'),
    min_length: z.number().int().nonnegative().nullable().default(null),
    max_length: z.number().int().positive().nullable().default(null),
  })
  .strict();

const LongTextBlock = z
  .object({
    type: z.literal('long_text'),
    block_id: UuidSchema,
    label: TextContentSchema,
    required: z.boolean().default(false),
    min_length: z.number().int().nonnegative().nullable().default(null),
    max_length: z.number().int().positive().nullable().default(null),
  })
  .strict();

const SingleChoiceBlock = z
  .object({
    type: z.literal('single_choice'),
    block_id: UuidSchema,
    label: TextContentSchema,
    required: z.boolean().default(false),
    presentation: z.enum(['list', 'scale']).default('list'),
    option_ids: z.array(UuidSchema).default([]),
    options_by_id: z.record(UuidSchema, OptionSchema).default({}),
  })
  .strict();

const CheckboxesBlock = z
  .object({
    type: z.literal('checkboxes'),
    block_id: UuidSchema,
    label: TextContentSchema,
    required: z.boolean().default(false),
    min_selections: z.number().int().nonnegative().nullable().default(null),
    max_selections: z.number().int().nonnegative().nullable().default(null),
    option_ids: z.array(UuidSchema).default([]),
    options_by_id: z.record(UuidSchema, OptionSchema).default({}),
  })
  .strict();

const SectionBreakBlock = z
  .object({
    type: z.literal('section_break'),
    block_id: UuidSchema,
    section_id: UuidSchema,
    heading: TextContentSchema.nullable().default(null),
  })
  .strict();

const PageBreakBlock = z
  .object({
    type: z.literal('page_break'),
    block_id: UuidSchema,
    page_id: UuidSchema,
    heading: TextContentSchema.nullable().default(null),
  })
  .strict();

const HiddenFieldBlock = z
  .object({
    type: z.literal('hidden_field'),
    block_id: UuidSchema,
    label: TextContentSchema,
    key: z.string().min(1).max(128),
    default_value: z.string().max(2048).nullable().default(null),
  })
  .strict();

export const BlockSchema = z.discriminatedUnion('type', [
  HeadingBlock,
  TextBlock,
  ShortTextBlock,
  LongTextBlock,
  SingleChoiceBlock,
  CheckboxesBlock,
  SectionBreakBlock,
  PageBreakBlock,
  HiddenFieldBlock,
]);
export type Block = z.infer<typeof BlockSchema>;

export const BLOCK_TYPES = [
  'heading', 'text', 'short_text', 'long_text', 'single_choice', 'checkboxes',
  'section_break', 'page_break', 'hidden_field',
] as const;
export type BlockType = (typeof BLOCK_TYPES)[number];

export const PREDICATE_ALLOWED_BLOCK_TYPES: Record<string, readonly BlockType[]> = {
  text_equals: ['short_text', 'long_text'],
  text_contains: ['short_text', 'long_text'],
  text_length_min: ['short_text', 'long_text'],
  text_length_max: ['short_text', 'long_text'],
  option_selected: ['single_choice', 'checkboxes'],
  option_not_selected: ['single_choice', 'checkboxes'],
  checkbox_count_min: ['checkboxes'],
  checkbox_count_max: ['checkboxes'],
  hidden_value_equals: ['hidden_field'],
};
